import type React from "react";
import { createContext, useEffect, useMemo, useState } from "react";
import Pusher from "pusher-js";

interface PusherContextType {
	client: Pusher | null;
	isConnected: boolean;
}

export const PusherContext = createContext<PusherContextType | null>(null);

interface PusherProviderProps {
	clientKey: string;
	cluster: string;
	authEndpoint: string;
	auth: {
		params: {
			store: string;
			mode: string;
		};
	};
	children: React.ReactNode;
}

export const PusherProvider: React.FC<PusherProviderProps> = ({
	clientKey,
	cluster,
	authEndpoint,
	auth,
	children,
}) => {
	const [isConnected, setIsConnected] = useState(false);

	// Initialize Pusher client once
	const client = useMemo(() => {
		const pusherClient = new Pusher(clientKey, {
			cluster,
			authEndpoint,
			auth,
		});

		return pusherClient;
	}, [clientKey, cluster, authEndpoint, auth]);

	// Track connection state
	useEffect(() => {
		const handleStateChange = (states: {
			current: string;
			previous: string;
		}) => {
			setIsConnected(states.current === "connected");
		};

		client.connection.bind("state_change", handleStateChange);

		return () => {
			client.connection.unbind("state_change", handleStateChange);
			client.disconnect();
		};
	}, [client]);

	const value = useMemo(
		() => ({
			client,
			isConnected,
		}),
		[client, isConnected],
	);

	return (
		<PusherContext.Provider value={value}>{children}</PusherContext.Provider>
	);
};
